// /reports/discounts — Discount Analysis (PAGES.md §3.48)

import { useState, useMemo } from 'react';
import type { ColumnDef, TimeRangeValue } from '@bbpos/ui';
import { formatPaise } from '@/utils/currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import {
  ReportPageShell,
  ReportFilterBar,
  ReportKpiRow,
  ReportTable,
  ReportExportMenu,
  ReportSkeleton,
  ReportError,
  useReportFilter,
  useReportQuery,
  reportsApi,
  downloadCsv,
  shareReportLink,
  DEFAULT_REPORT_PRESET,
  KpiIcons,
} from '@/features/reports';
import type { DiscountTypeRow, DiscountedProductRow, DiscountStaffRow } from '@/features/reports';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';

type TypeRow = DiscountTypeRow & { id: string };
type ProductRow = DiscountedProductRow & { id: string };
type StaffRow = DiscountStaffRow & { id: string };

function makeTypeColumns(currency: CurrencyCode): ColumnDef<TypeRow>[] {
  return [
    { key: 'type', header: 'Discount Type', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.type.replace('_', ' ')}</span> },
    { key: 'count', header: 'Count', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.count.toLocaleString('en-IN')}</span> },
    { key: 'total', header: 'Total', numeric: true, render: (_v, row) => <span className="text-sm font-semibold text-warning-700 dark:text-warning-300 tabular-nums">{formatPaise(row.total, currency)}</span> },
    { key: 'avg', header: 'Avg', numeric: true, render: (_v, row) => <span className="tabular-nums">{formatPaise(row.avg, currency)}</span> },
  ];
}

function makeProductColumns(currency: CurrencyCode): ColumnDef<ProductRow>[] {
  return [
    { key: 'product_name', header: 'Product', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.product_name}</span> },
    { key: 'bill_count', header: 'Bills', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.bill_count.toLocaleString('en-IN')}</span> },
    { key: 'discount_total', header: 'Discount', numeric: true, render: (_v, row) => <span className="text-sm font-semibold text-warning-700 dark:text-warning-300 tabular-nums">{formatPaise(row.discount_total, currency)}</span> },
  ];
}

function makeStaffColumns(currency: CurrencyCode): ColumnDef<StaffRow>[] {
  return [
    { key: 'user_name', header: 'Staff', render: (_v, row) => <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">{row.user_name}</span> },
    { key: 'discount_count', header: 'Bills', numeric: true, render: (_v, row) => <span className="tabular-nums">{row.discount_count.toLocaleString('en-IN')}</span> },
    { key: 'total_discount', header: 'Discount Given', numeric: true, render: (_v, row) => <span className="text-sm font-semibold text-warning-700 dark:text-warning-300 tabular-nums">{formatPaise(row.total_discount, currency)}</span> },
  ];
}

export function DiscountAnalysisPage() {
  const [timeRange, setTimeRange] = useState<TimeRangeValue>(DEFAULT_REPORT_PRESET);
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const filter = useReportFilter(timeRange, locationId);
  const currency = useTenantCurrency();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const csvDecimals = Math.round(Math.log10(subunit));
  const typeColumns = useMemo(() => makeTypeColumns(currency), [currency]);
  const productColumns = useMemo(() => makeProductColumns(currency), [currency]);
  const staffColumns = useMemo(() => makeStaffColumns(currency), [currency]);

  const { data, isLoading, error } = useReportQuery({
    key: 'discounts',
    filter,
    queryFn: (f) => reportsApi.getDiscountAnalysis(f),
  });

  const staffQuery = useReportQuery({
    key: 'discounts-by-staff',
    filter,
    queryFn: (f) => reportsApi.getDiscountsByStaff(f),
  });
  const staffRows: StaffRow[] = useMemo(
    () => (staffQuery.data ?? []).map((s) => ({ ...s, id: s.user_name })),
    [staffQuery.data],
  );

  const typeRows: TypeRow[] = useMemo(
    () => (data?.by_type ?? []).map((t) => ({ ...t, id: t.type })),
    [data],
  );
  const productRows: ProductRow[] = useMemo(
    () => (data?.top_products ?? []).map((p) => ({ ...p, id: p.product_id })),
    [data],
  );

  const handleExport = (fmt: 'csv' | 'pdf' | 'whatsapp' | 'email') => {
    if (fmt === 'pdf') {
      // Browser print dialog -> "Save as PDF"; no server-side PDF renderer exists.
      window.print();
      return;
    }
    if (fmt === 'csv') {
      // One file for both tables on the page: a Section column tells the by-type rows apart
      // from the top discounted products.
      type ExportRow = { section: string; name: string; count: number; total: number; avg?: number };
      const exportRows: ExportRow[] = [
        ...typeRows.map((r) => ({ section: 'By Discount Type', name: r.type, count: r.count, total: r.total, avg: r.avg })),
        ...productRows.map((r) => ({ section: 'Top Discounted Products', name: r.product_name, count: r.bill_count, total: r.discount_total })),
      ];
      downloadCsv(
        `discount-analysis-${filter.range.from}-to-${filter.range.to}.csv`,
        [
          { header: 'Section', get: (r: ExportRow) => r.section },
          { header: 'Type / Product', get: (r: ExportRow) => r.name },
          { header: 'Count / Bills', get: (r: ExportRow) => r.count },
          { header: `Total (${symbol})`, get: (r: ExportRow) => (r.total / subunit).toFixed(csvDecimals) },
          { header: `Avg (${symbol})`, get: (r: ExportRow) => (r.avg === undefined ? '' : (r.avg / subunit).toFixed(csvDecimals)) },
        ],
        exportRows,
      );
      return;
    }
    if (fmt === 'whatsapp' || fmt === 'email') {
      shareReportLink(fmt, 'Orio360 Discount Analysis', `Total discounts: ${formatPaise(data?.total_discount ?? 0, currency)} (${(data?.discount_pct ?? 0).toFixed(1)}% of sales)`);
    }
  };

  return (
    <ReportPageShell
      title="Discount Analysis"
      subtitle="Line, bill and voucher discount breakdown — find margin leakage."
      breadcrumbs={[{ label: 'Home', href: '/home' }, { label: 'Reports', href: '/reports' }, { label: 'Discounts' }]}
      accent="warning-danger"
      headerActions={<ReportExportMenu onExport={handleExport} disabled={!typeRows.length && !productRows.length} />}
      filterBar={<ReportFilterBar timeRange={timeRange} onTimeRangeChange={setTimeRange} locationId={locationId} onLocationChange={setLocationId} />}
    >
      {isLoading ? (
        <ReportSkeleton kpiCount={3} />
      ) : error ? (
        <ReportError />
      ) : (
        <>
          <ReportKpiRow
            tiles={[
              { id: 'total', title: 'Total Discounts', value: formatPaise(data?.total_discount ?? 0, currency), tone: 'warning', icon: KpiIcons.tag },
              { id: 'pct', title: 'Discount % of Sales', value: `${(data?.discount_pct ?? 0).toFixed(1)}%`, tone: 'danger', icon: KpiIcons.percent },
              { id: 'avg', title: 'Avg / Bill', value: formatPaise(data?.avg_discount_per_bill ?? 0, currency), tone: 'brand', icon: KpiIcons.receipt },
            ]}
          />
          <ReportTable title="By Discount Type" columns={typeColumns} data={typeRows} emptyTitle="No discounts applied in selected period" totalCount={typeRows.length} />
          <ReportTable title="By Staff" columns={staffColumns} data={staffRows} emptyTitle="No staff discounts in selected period" totalCount={staffRows.length} />
          <ReportTable title="Top Discounted Products" columns={productColumns} data={productRows} emptyTitle="No discounted products" totalCount={productRows.length} />
        </>
      )}
    </ReportPageShell>
  );
}
